/**
 * Crunch Design System — Stepper & StepperStep
 *
 * Figma components:
 *   stepper      (component, node 3936:6629)
 *   stepper/step (component set, node 4006:8559)
 *
 * Usage:
 *   import { faCircle1, faCircle2, faCircle3 } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';
 *
 *   <Stepper>
 *     <StepperStep icon={faCircle1} label="Upload" active />
 *     <StepperStep icon={faCircle2} label="Review" />
 *     <StepperStep icon={faCircle3} label="Confirm" />
 *   </Stepper>
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

export const Stepper = React.forwardRef<HTMLElement, {
    children?: React.ReactNode;
    className?: string;
    [key: string]: any;
}>(function Stepper(
    { children, className, ...rest },
    ref,
) {
    const cls = ["cr-stepper", className].filter(Boolean).join(" ");
    return (
        <nav ref={ref as React.Ref<HTMLElement>} className={cls} aria-label="Progress" {...rest}>
            <ol className="cr-stepper__list">{children}</ol>
        </nav>
    );
});

export const StepperStep = React.forwardRef<HTMLLIElement, {
    icon?: IconDefinition;
    label?: string;
    active?: boolean;
    completed?: boolean;
    className?: string;
    [key: string]: any;
}>(function StepperStep(
    { icon, label = "Step", active = false, completed = false, className, ...rest },
    ref,
) {
    const cls = [
        "cr-stepper__step",
        active && "cr-stepper__step--active",
        completed && "cr-stepper__step--completed",
        className,
    ].filter(Boolean).join(" ");

    const resolvedIcon = completed ? faCircleCheck : icon;

    return (
        <li ref={ref} className={cls} aria-current={active ? "step" : undefined} {...rest}>
            {resolvedIcon && (
                <FontAwesomeIcon icon={resolvedIcon} className="cr-stepper__step-icon" aria-hidden />
            )}
            <span className="cr-stepper__step-label">{label}</span>
        </li>
    );
});
